import { api } from '../../../shared/lib/axios';

/** Endereços, contatos, contas bancárias e fila de validação. */

export type AddressType = 'residencial' | 'correspondencia';

export interface Address {
  id: string;
  type: AddressType;
  zipCode: string;
  street: string;
  number: string;
  complement: string | null;
  district: string;
  stateUf: string;
  cityId: number;
  cityName: string;
  country: string;
  validFrom: string;
  validTo: string | null;
  status: 'pendente' | 'vigente' | 'encerrado' | 'rejeitado';
  source: 'dp' | 'portal';
  rejectionReason: string | null;
}

export interface AddressPayload {
  type: AddressType;
  zipCode: string;
  street: string;
  number: string;
  complement?: string;
  district: string;
  stateUf: string;
  cityId: number;
  validFrom?: string;
}

export interface CepResult {
  zipCode: string;
  street: string;
  complement: string;
  district: string;
  stateUf: string;
  cityId: number | null;
  cityName: string;
}

export type ContactKind = 'telefone' | 'whatsapp' | 'email_pessoal';

export interface Contact {
  id: string;
  kind: ContactKind;
  value: string;
  isPrimary: boolean;
}

export interface EmergencyContact {
  id: string;
  name: string;
  relationship: string;
  phone: string | null;
  mobile: string | null;
  email: string | null;
  notes: string | null;
  isPrimary: boolean;
}

export interface EmergencyPayload {
  name: string;
  relationship: string;
  phone?: string | null;
  mobile?: string | null;
  email?: string | null;
  notes?: string | null;
  isPrimary?: boolean;
}

export interface Bank {
  code: string;
  ispb: string;
  name: string;
}

export interface BankAccount {
  id: string;
  bankCode: string | null;
  bankName: string;
  agency: string;
  agencyDigit: string | null;
  account: string;
  accountDigit: string | null;
  masked: boolean;
  accountType: 'corrente' | 'poupanca' | 'salario' | 'pagamento';
  pixKey: string | null;
  holderName: string;
  holderCpf: string;
  holderDivergent: boolean;
  isPrimary: boolean;
  status: 'pendente_validacao' | 'validada' | 'rejeitada' | 'encerrada';
  validFrom: string;
  validTo: string | null;
  validationNote: string | null;
  rejectionReason: string | null;
  source: 'dp' | 'portal' | 'legado';
}

export interface BankAccountPayload {
  bankCode: string;
  agency: string;
  agencyDigit?: string;
  account: string;
  accountDigit?: string;
  accountType: BankAccount['accountType'];
  pixKey?: string;
  holderName?: string;
  holderCpf?: string;
}

export interface ValidationQueue {
  documents: Array<{
    id: string; userId: string; personName: string; title: string; typeCode: string; status: string;
    version: number; confidentiality: string; uploadedAt: string;
  }> | null;
  addresses: Array<{
    id: string; userId: string; personName: string; type: AddressType; zipCode: string; street: string; number: string;
    district: string; cityName: string; stateUf: string; validFrom: string; createdAt: string;
  }> | null;
  bankAccounts: Array<{
    id: string; userId: string; personName: string; bankCode: string | null; bankName: string; agency: string;
    account: string; holderName: string; holderDivergent: boolean; source: string; createdAt: string;
  }> | null;
}

function list<T>(body: unknown): T[] {
  if (Array.isArray(body)) return body as T[];
  const data = (body as { data?: unknown } | null)?.data;
  return Array.isArray(data) ? (data as T[]) : [];
}

function object<T>(body: unknown): T {
  const { version: _v, _links: _l, ...rest } = (body ?? {}) as Record<string, unknown>;
  return rest as T;
}

/** Base das rotas: o DP usa `/collaborators/:id`, o portal usa `/me`. */
export type Owner = { kind: 'dp'; personId: string } | { kind: 'me' };
const base = (owner: Owner) => (owner.kind === 'me' ? '/me' : `/collaborators/${owner.personId}`);

export async function consultarCep(cep: string): Promise<CepResult> {
  const { data } = await api.get(`/geo/cep/${cep.replace(/\D/g, '')}`);
  return object<CepResult>(data);
}

export async function listarEnderecos(owner: Owner): Promise<Address[]> {
  const { data } = await api.get(`${base(owner)}/addresses`);
  return list<Address>(data);
}

export async function criarEndereco(owner: Owner, payload: AddressPayload): Promise<Address> {
  const { data } = await api.post(`${base(owner)}/addresses`, payload);
  return object<Address>(data);
}

export async function aprovarEndereco(id: string): Promise<Address> {
  const { data } = await api.post(`/collaborator-addresses/${id}/approve`);
  return object<Address>(data);
}

export async function rejeitarEndereco(id: string, reason: string): Promise<Address> {
  const { data } = await api.post(`/collaborator-addresses/${id}/reject`, { reason });
  return object<Address>(data);
}

export async function listarContatos(owner: Owner): Promise<{ contacts: Contact[]; emergencyContacts: EmergencyContact[] }> {
  const { data } = await api.get(`${base(owner)}/contacts`);
  const b = object<{ contacts?: Contact[]; emergencyContacts?: EmergencyContact[] }>(data);
  return { contacts: b.contacts ?? [], emergencyContacts: b.emergencyContacts ?? [] };
}

export async function salvarContato(owner: Owner, id: string | null, payload: { kind: ContactKind; value: string; isPrimary?: boolean }) {
  const { data } = id
    ? await api.patch(`${base(owner)}/contacts/${id}`, payload)
    : await api.post(`${base(owner)}/contacts`, payload);
  return object<Contact>(data);
}

export async function removerContato(owner: Owner, id: string): Promise<void> {
  await api.delete(`${base(owner)}/contacts/${id}`);
}

export async function salvarContatoEmergencia(owner: Owner, id: string | null, payload: EmergencyPayload) {
  const { data } = id
    ? await api.patch(`${base(owner)}/emergency-contacts/${id}`, payload)
    : await api.post(`${base(owner)}/emergency-contacts`, payload);
  return object<EmergencyContact>(data);
}

export async function removerContatoEmergencia(owner: Owner, id: string): Promise<void> {
  await api.delete(`${base(owner)}/emergency-contacts/${id}`);
}

export async function listarBancos(owner: Owner): Promise<Bank[]> {
  const { data } = await api.get(owner.kind === 'me' ? '/me/banks' : '/banks');
  return list<Bank>(data);
}

export async function listarContas(owner: Owner): Promise<BankAccount[]> {
  const { data } = await api.get(`${base(owner)}/bank-accounts`);
  return list<BankAccount>(data);
}

export async function criarConta(owner: Owner, payload: BankAccountPayload): Promise<BankAccount> {
  const { data } = await api.post(`${base(owner)}/bank-accounts`, payload);
  return object<BankAccount>(data);
}

export async function validarConta(id: string, note?: string): Promise<BankAccount> {
  const { data } = await api.post(`/collaborator-bank-accounts/${id}/validate`, { note });
  return object<BankAccount>(data);
}

export async function rejeitarConta(id: string, reason: string): Promise<BankAccount> {
  const { data } = await api.post(`/collaborator-bank-accounts/${id}/reject`, { reason });
  return object<BankAccount>(data);
}

export async function contaPrincipal(id: string): Promise<BankAccount> {
  const { data } = await api.post(`/collaborator-bank-accounts/${id}/primary`);
  return object<BankAccount>(data);
}

export async function encerrarConta(id: string): Promise<BankAccount> {
  const { data } = await api.post(`/collaborator-bank-accounts/${id}/close`);
  return object<BankAccount>(data);
}

export async function filaDeValidacao(): Promise<ValidationQueue> {
  const { data } = await api.get('/people/validation-queue');
  const b = object<ValidationQueue>(data);
  return { documents: b.documents ?? null, addresses: b.addresses ?? null, bankAccounts: b.bankAccounts ?? null };
}
